import type { Manifest } from '@clawharbor/manifest';
import { html, SP } from '../html.js';
import type { VNode } from '../html.js';
import type { Actions } from '../actions.js';
import type { ConsoleState } from '../store.js';
import type { Clearance, PublishFailure } from '../types.js';
import { CopyButton } from './copy.js';
import { OwnershipPanel } from '../dashboard/ui/ownership.js';

/**
 * The publish gate.
 *
 * This is the only control in the console that changes the world, and the
 * screen it sits on is a review, not a form. So the gate does not summarise —
 * it enumerates. Two columns, side by side: everything that becomes readable by
 * anyone with the URL, and everything that does not leave this machine. Both
 * columns are generated from the manifest itself, so they cannot drift from
 * what is actually served.
 *
 * The disclaimer is shown as the exact string that will appear, not a
 * description of it, and it is copyable — an operator who has to clear that
 * text with someone else should be able to paste it, not retype it.
 *
 * Below the ledger, the gate is decided by WHO is asking, as `/api/auth/me`
 * answered once for this screen:
 *
 *   nobody signed in            a sign-in form; the operator's secret behind a
 *                               collapsed heading, because that path still
 *                               exists and hiding it would only move it
 *   an account, third-party     no publish button at all — the operator
 *                               publishes those, once permission is on record —
 *                               and a mail to ask, with the job id in it
 *   an account, self-owned,     publish is present and refused until the
 *   no current claim            domain is verified; the three proofs, verbatim
 *   an account, claim current   publish, on the session, no secret
 *
 * Every refusal is the API's own sentence plus the step it names. There is no
 * "publish failed" here: the server always says what to do next, and a console
 * that summarised it as failure would be discarding the useful half.
 */

/** The path shape apps/server routes. The host belongs to whoever runs it. */
function endpointPaths(tenantId: string): { web: string; mcp: string } {
  return { web: `/t/${tenantId}`, mcp: `/t/${tenantId}/mcp` };
}

/**
 * Where a publication request goes.
 *
 * The address the site publishes on its imprint (apps/site/legal.mjs), which
 * is the one a person could find without this console. A self-hosted console
 * can name its own operator in `<meta name="clawharbor-contact">`; nothing
 * here invents an inbox or a queue behind one.
 */
const IMPRINT_CONTACT = 'hello@clawharbor.io';

function contactAddress(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="clawharbor-contact"]');
  return meta?.content?.trim() || IMPRINT_CONTACT;
}

function Line(props: { tone: 'in' | 'out'; head: VNode | string; body: VNode | string }): VNode {
  return html`
    <li class=${props.tone === 'in' ? 'ledger2__row' : 'ledger2__row ledger2__row--out'}>
      <span class="ledger2__glyph" aria-hidden="true">${props.tone === 'in' ? '+' : '−'}</span>
      <div>
        <p class="ledger2__head">${props.head}</p>
        <p class="ledger2__body">${props.body}</p>
      </div>
    </li>
  `;
}

export function PublishGate(props: {
  state: ConsoleState;
  actions: Actions;
  manifest: Manifest;
  turnedBack: Clearance[];
}): VNode {
  const { state, actions, manifest: m, turnedBack } = props;
  const thirdParty = m.policy.ownership === 'third-party';
  const disclaimer = m.policy.disclaimer;
  const paths = endpointPaths(m.id);
  const tokenCount = Object.values(m.brand.palette).filter((t) => t != null).length;
  const sans = m.brand.typography.families.sans;
  const face = sans.substitute?.family ?? sans.observed;
  const ttlMin = Math.round((m.capabilities[0]?.freshness.ttlSec ?? 1800) / 60);
  const names = m.capabilities.map((c) => c.name);

  return html`
    <div class="gate">
      <h2 class="gate__h">You are the gate.</h2>
      <p class="gate__p">
        Nothing on this page is live. Publishing serves this manifest, unchanged, at two public URLs
        until you take it down.${SP}
        ${state.mode === 'demo'
          ? 'This session is a replay of a recorded crawl, so confirming here publishes nothing.'
          : ''}
      </p>

      <div class="ledger2">
        <div class="ledger2__col">
          <p class="ledger2__cap ledger2__cap--in">Becomes public</p>
          <ul>
            ${Line({
              tone: 'in',
              head: html`<code>${paths.web}</code> — a branded page`,
              body: html`Your palette and type, registering ${names.length} tools on${SP}
                <code>document.modelContext</code>, for any browser agent that opens it. The host
                is whoever runs this server.`,
            })}
            ${Line({
              tone: 'in',
              head: html`<code>${paths.mcp}</code> — a remote MCP endpoint`,
              body: `Anyone who pastes that URL into an MCP client connects, with no key and no
                account, and can call all ${names.length} tools.`,
            })}
            ${Line({
              tone: 'in',
              head: `${names.length} tool ${names.length === 1 ? 'name' : 'names'} and their full descriptions`,
              body: html`Readable by every agent that connects:${SP}
                <b class="ledger2__names">${names.join(', ')}</b>. The descriptions above are served
                verbatim — they are what an agent reads to decide when to call each one.`,
            })}
            ${Line({
              tone: 'in',
              head: `${tokenCount} colour tokens, ${face}, and the extracted radii`,
              body: `The specimen above, as CSS custom properties on both surfaces.`,
            })}
            ${Line({
              tone: 'in',
              head: `Live data from ${m.source.canonicalOrigin}`,
              body: `Fetched on demand at up to ${m.policy.data.maxRps}/s and cached for ${ttlMin}
                minutes. Every call reaches the origin; nothing is served from a copy older than
                that.`,
            })}
          </ul>
        </div>

        <div class="ledger2__col">
          <p class="ledger2__cap ledger2__cap--out">Does not</p>
          <ul>
            ${turnedBack.length
              ? Line({
                  tone: 'out',
                  head: `${turnedBack.length} turned-back ${turnedBack.length === 1 ? 'proposal' : 'proposals'}`,
                  body: html`<b class="ledger2__names">${turnedBack.map((c) => c.id).join(', ')}</b>${SP}
                    — recorded on this page and excluded from both surfaces. No agent will ever see
                    them.`,
                })
              : Line({
                  tone: 'out',
                  head: 'Nothing was turned back on this crawl',
                  body: 'Every proposal executed against the live origin and returned rows.',
                })}
            ${Line({
              tone: 'out',
              head: 'No history of who asked what',
              body: `Results are not retained (${m.policy.data.retainHistory ? 'retainHistory: true' : 'retainHistory: false'}).
                Neither surface stores a visitor, a session, or a query.`,
            })}
            ${thirdParty
              ? Line({
                  tone: 'out',
                  head: 'No logo file, no font binary, no rehosted text',
                  body: `This site is not yours, so it runs under third-party rules: the wordmark is
                    set in type, the face is a licensed substitute, and every transaction completes
                    on ${m.source.canonicalOrigin}.`,
                })
              : Line({
                  tone: 'out',
                  head: 'Registered as your own property',
                  body: 'Your assets, your checkout, your terms.',
                })}
            ${Line({
              tone: 'out',
              head: 'No tool that writes, books, pays, or authenticates',
              body: html`The strongest kind generated is <code>stage</code>, which returns a deep
                link a person has to confirm on ${m.source.canonicalOrigin}.`,
            })}
          </ul>
        </div>
      </div>

      ${disclaimer
        ? html`
            <div class="disclaimer">
              <div class="disclaimer__bar">
                <span class="disclaimer__tag">
                  Exact text · above the fold on both surfaces · not dismissible
                </span>
                ${CopyButton({
                  id: 'disclaimer',
                  text: disclaimer,
                  copied: state.copied,
                  onCopy: actions.copy,
                  describe: 'the disclaimer',
                })}
              </div>
              <p class="disclaimer__text">${disclaimer}</p>
            </div>
          `
        : null}

      ${Standing({ state, actions, manifest: m, names })}
    </div>
  `;
}

/**
 * The part of the gate that depends on who is asking.
 *
 * Until `/api/auth/me` has answered there is nothing honest to show below the
 * disclaimer, so nothing is: a sign-in form that turns into a publish button
 * half a second later would be the console guessing, and guessing wrong under
 * the reader's cursor.
 */
function Standing(props: {
  state: ConsoleState;
  actions: Actions;
  manifest: Manifest;
  names: string[];
}): VNode {
  const { state, actions, manifest: m, names } = props;
  if (!state.principalChecked) {
    return html`<p class="secret__hint">Checking whether you are signed in…</p>`;
  }

  const principal = state.principal;
  if (!principal) return SignedOut({ state, actions, names });

  const who = principal.kind === 'account' ? principal.email : 'the operator';
  const thirdParty = m.policy.ownership === 'third-party';
  if (principal.kind === 'account' && thirdParty) return ThirdParty({ state, manifest: m, who });

  const verified =
    principal.kind === 'operator' ||
    state.claim?.claim?.current === true ||
    state.verification?.verified === true;
  return html`
    <div>
      ${verified
        ? html`<p class="secret__hint">
            Signed in as ${who}.${SP}
            ${principal.kind === 'operator'
              ? 'The operator header is on this request; no domain claim is needed.'
              : state.claim?.claim
                ? `Control of ${state.claim.claim.domain} is verified (${state.claim.claim.method}), good for another ${state.claim.claim.daysUntilExpiry} days.`
                : `Control of ${hostOf(m.source.canonicalOrigin)} is verified.`}
          </p>`
        : Unproven({ state, actions, manifest: m, who })}
      ${Ack({ state, actions, names, disclaimer: m.policy.disclaimer })}
      ${PublishButton({ state, actions, names })}
      ${Refusal({ state, actions, manifest: m })}
    </div>
  `;
}

/**
 * Nobody signed in.
 *
 * Publishing needs an account or the host's secret. The account is the path a
 * stranger can take, so it is the one on top; the secret path is kept, folded,
 * because the operator still exists and the recorded replay has no other way
 * to press its harmless button.
 */
function SignedOut(props: { state: ConsoleState; actions: Actions; names: string[] }): VNode {
  const { state, actions, names } = props;
  const sent = state.loginSent;
  const replay = state.mode === 'demo';

  return html`
    <div>
      <div class="secret">
        <h3 class="gate__h" style="font-size:1.0625rem;margin-bottom:8px">Publishing needs an account.</h3>
        ${sent
          ? html`
              <p class="gate__p" style="margin-bottom:10px">
                Check your inbox. If ${state.loginEmail.trim()} has an account, a sign-in link is on
                its way; it works once and expires. Signing in opens the dashboard — this draft is
                kept as job <code>${state.jobId}</code>.
              </p>
              ${sent.devLink
                ? html`
                    <div class="disclaimer">
                      <div class="disclaimer__bar">
                        <span class="disclaimer__tag">Development mode · the link the mail would carry</span>
                        ${CopyButton({
                          id: 'devlink',
                          text: sent.devLink,
                          copied: state.copied,
                          onCopy: actions.copy,
                          describe: 'the sign-in link',
                        })}
                      </div>
                      <p class="disclaimer__text">
                        This host's email provider is <code>log</code>, so nothing was mailed; the
                        link is printed here instead.${SP}
                        <a href=${sent.devLink}>Continue with this link</a>
                      </p>
                    </div>
                  `
                : null}
            `
          : html`
              <form
                onSubmit=${(e: Event) => {
                  e.preventDefault();
                  actions.login();
                }}
              >
                <label class="secret__label" for="login-email">Email</label>
                <input
                  id="login-email"
                  class="secret__input"
                  type="email"
                  autocomplete="email"
                  spellcheck="false"
                  placeholder="you@example.com"
                  value=${state.loginEmail}
                  aria-invalid=${state.loginError ? 'true' : 'false'}
                  aria-describedby="login-hint"
                  disabled=${replay}
                  onInput=${(e: Event) => actions.setLoginEmail((e.target as HTMLInputElement).value)}
                />
                <div class="gate__actions" style="margin-top:12px">
                  <button type="submit" class="btn" disabled=${replay || state.loginSending}>
                    ${state.loginSending ? 'Sending…' : 'Email me a sign-in link'}
                  </button>
                </div>
                ${state.loginError ? html`<p class="gate__err" role="alert">${state.loginError}</p>` : null}
                <p class="secret__hint" id="login-hint">
                  ${replay
                    ? 'This build has no account service behind it, so no link can be sent.'
                    : 'No password. The link signs this browser in for 30 days; the address is used for nothing else.'}
                </p>
              </form>
            `}
      </div>

      <details class="secret">
        <summary class="secret__label" style="cursor:pointer;display:list-item">I am the operator</summary>
        ${state.mode === 'live'
          ? html`
              <label class="secret__label" for="publish-secret" style="margin-top:12px">Publish secret</label>
              <input
                id="publish-secret"
                class="secret__input"
                type="password"
                autocomplete="off"
                spellcheck="false"
                placeholder="CLAWHARBOR_RELOAD_SECRET"
                value=${state.publishSecret}
                onInput=${(e: Event) => actions.setPublishSecret((e.target as HTMLInputElement).value)}
              />
              <p class="secret__hint">
                Going live needs the secret this host was started with. It is sent with this one
                request and never stored.
              </p>
            `
          : html`<p class="secret__hint" style="margin-top:12px">
              The replay asks for no secret: there is no host behind it to publish to.
            </p>`}
        ${Ack({ state, actions, names, disclaimer: null })}
        ${PublishButton({ state, actions, names })}
      </details>
      ${state.publishError ? Refusal({ state, actions, manifest: null }) : null}
    </div>
  `;
}

/**
 * An account looking at somebody else's brand.
 *
 * There is no button because the server would refuse it: third-party drafts
 * are published by the operator, once the brand's permission is on record. The
 * request is a mail, prefilled with what the operator needs to find the draft,
 * because a "request queue" would be a promise this host does not keep.
 */
function ThirdParty(props: { state: ConsoleState; manifest: Manifest; who: string }): VNode {
  const { state, manifest: m, who } = props;
  const to = contactAddress();
  const subject = `Publication request: ${m.id}`;
  const body = [
    `Job: ${state.jobId ?? m.id}`,
    `Tenant: ${m.id}`,
    `Site: ${m.source.canonicalOrigin}`,
    `Requested by: ${who}`,
    '',
    'Permission from the brand (who granted it, and how it can be checked):',
    '',
  ].join('\n');
  const href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return html`
    <div class="secret" style="border-top:1px solid var(--rule);padding-top:18px">
      <p class="gate__p">
        Signed in as ${who}. This is somebody else's brand — the draft is headed with the
        disclaimer above and runs under third-party rules — and drafts like it are published by
        the operator, once the brand's permission is on record. Nothing here can put it live.
      </p>
      <div class="gate__actions">
        <a class="btn" href=${href}>Request publication</a>
      </div>
      <p class="secret__hint">
        Opens a mail to ${to} with job <code>${state.jobId ?? m.id}</code> in it. There is no queue
        behind this button; the mail is the request.
      </p>
    </div>
  `;
}

/**
 * An account, its own site, and no current claim on record.
 *
 * The proofs, the attempt list and the two buttons are `dashboard/ui/ownership.ts`
 * now — the same component the dashboard's site screen mounts. They lived here
 * and only here for the whole life of the feature, which is why the only way a
 * customer could prove or renew a domain was to run a fresh crawl and land back
 * on this screen. What stays here is the sentence that is true only here: this
 * is a draft, and publish is what is being refused.
 */
function Unproven(props: { state: ConsoleState; actions: Actions; manifest: Manifest; who: string }): VNode {
  const { state, actions, manifest: m, who } = props;
  const claim = state.claim?.claim ?? null;
  const open = state.claim?.openChallenge ?? null;

  return html`
    <div class="secret" style="border-top:1px solid var(--rule);padding-top:18px">
      ${OwnershipPanel({
        domain: hostOf(m.source.canonicalOrigin),
        claim,
        openChallenge: open,
        challenge: state.challenge,
        verification: state.verification,
        issuing: state.challengeBusy,
        checking: state.verifying,
        error: state.challengeError,
        copied: state.copied,
        onCopy: actions.copy,
        onIssue: () => actions.requestProofs(),
        onCheck: () => actions.verifyOwnership(),
        surface: 'console',
        lede: html`Signed in as ${who}. This draft publishes as your own site, with no disclaimer,
          so publish is refused until control of the domain is verified. Verifying publishes
          nothing by itself.`,
      })}
    </div>
  `;
}

function Ack(props: {
  state: ConsoleState;
  actions: Actions;
  names: string[];
  disclaimer: string | null | undefined;
}): VNode {
  const { state, actions, names, disclaimer } = props;
  return html`
    <label class="ack">
      <input
        class="ack__box"
        type="checkbox"
        checked=${state.acknowledged}
        onChange=${() => actions.toggleAck()}
      />
      <span class="ack__text">
        I have read the ${names.length} tool${SP}
        ${names.length === 1 ? 'description' : 'descriptions'} above${
          disclaimer ? ' and the disclaimer' : ''
        }, and I am authorised to publish this manifest.
      </span>
    </label>
  `;
}

function PublishButton(props: { state: ConsoleState; actions: Actions; names: string[] }): VNode {
  const { state, actions, names } = props;
  return html`
    <button
      type="button"
      class="btn btn--block"
      disabled=${!state.acknowledged || state.publishing}
      onClick=${() => actions.publish()}
    >
      ${state.publishing ? 'Publishing…' : `Publish ${names.length} tools on two public URLs`}
    </button>
  `;
}

/**
 * The API's sentence, then the step it names.
 *
 * `code` is the server's word for the refusal, and each one already has a
 * control on this page — the proofs, the mail, the sign-in form — so the next
 * step is a pointer to it rather than a second explanation.
 */
function Refusal(props: { state: ConsoleState; actions: Actions; manifest: Manifest | null }): VNode | null {
  const { state, actions, manifest: m } = props;
  const failure: PublishFailure | null = state.publishError;
  if (!failure) return null;

  let next: VNode | null = null;
  switch (failure.code) {
    case 'ownership_unproven':
      next = state.challenge
        ? html`Next: publish one of the three proofs above and press “Verify now”.`
        : html`Next:${SP}
            <button type="button" class="linkish" onClick=${() => actions.requestProofs()}>
              show the three proofs
            </button>, publish one, and verify.`;
      break;
    case 'third_party_requires_operator':
      next = html`Next: this draft is published by the operator, once permission is on record —
        ${m ? html`use “Request publication” above.` : html`mail ${contactAddress()} with the job id.`}`;
      break;
    case 'unauthorized':
      next = html`Next: sign in above, or open “I am the operator” and enter the host's secret.`;
      break;
    default:
      next = null;
  }

  return html`
    <div>
      <p class="gate__err" role="alert">${failure.message}</p>
      ${next ? html`<p class="secret__hint">${next}</p>` : null}
    </div>
  `;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
